import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { FRONT_WARNING, LEAD_CARD_PATH, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useTaskListContext } from '@app/providers/TaskListProvider';
import { useLazyGetCrmTaskQuery } from '@app/services/rtkQuery/CrmTasks/api';
import {
  OverflowTooltip,
  useCloseFloatingOnScroll,
} from '@app/shared/components';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';
import { TagsOverflowList } from '@app/shared/ui/TagsOverflowList';

import { LEADS_REGISTRY_EMPTY_VALUE } from '../model/constants';
import { LeadInfoCell } from '../ui/components/LeadInfoCell';
import { LeadsRegistryFunnelStageCell } from '../ui/components/LeadsRegistryFunnelStageCell';
import { LeadsRegistryResponsibleCell } from '../ui/components/LeadsRegistryResponsibleCell';
import { ResponseTimeCell } from '../ui/components/ResponseTimeCell';

import { useLeadsRegistryFilterData } from './useLeadsRegistryFilterData';

import type {
  LeadsRegistryLegacyColumns,
  LeadsRegistryTableRow,
  LeadsRegistryTaskStatus,
} from '../model/types';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ColumnsType } from 'antd/es/table';
import type { FC, ReactNode } from 'react';

const renderSkeletonBlock = (className: string) => (
  <span
    aria-hidden="true"
    className={`LeadsRegistryTable__skeleton-block ${className}`}
  />
);

const renderLeadInfoSkeletonCell = () => (
  <div className="LeadsRegistryTable__lead-info-skeleton" aria-hidden="true">
    <div className="LeadsRegistryTable__lead-info-skeleton-main">
      {renderSkeletonBlock('LeadsRegistryTable__lead-info-skeleton-avatar')}
      <div className="LeadsRegistryTable__lead-info-skeleton-client">
        {renderSkeletonBlock(
          'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--client-name'
        )}
        {renderSkeletonBlock(
          'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--client-phone'
        )}
      </div>
    </div>
    <div className="LeadsRegistryTable__lead-info-skeleton-message">
      {renderSkeletonBlock('LeadsRegistryTable__skeleton-dot')}
      {renderSkeletonBlock('LeadsRegistryTable__skeleton-icon')}
      {renderSkeletonBlock(
        'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--message'
      )}
    </div>
  </div>
);

const renderFunnelSkeletonCell = () => (
  <div className="LeadsRegistryTable__funnel-skeleton" aria-hidden="true">
    <div className="LeadsRegistryTable__funnel-skeleton-row">
      <div className="LeadsRegistryTable__funnel-skeleton-chip">
        {renderSkeletonBlock(
          'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--funnel'
        )}
        {renderSkeletonBlock(
          'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--stage'
        )}
      </div>
      {renderSkeletonBlock('LeadsRegistryTable__funnel-skeleton-add')}
    </div>
    {renderSkeletonBlock('LeadsRegistryTable__funnel-skeleton-more')}
  </div>
);

const renderResponseTimeSkeletonCell = () => (
  <div className="LeadsRegistryTable__response-skeleton" aria-hidden="true">
    {renderSkeletonBlock('LeadsRegistryTable__response-skeleton-pill')}
  </div>
);

const renderResponsibleSkeletonCell = () => (
  <div className="LeadsRegistryTable__responsible-skeleton" aria-hidden="true">
    {renderSkeletonBlock(
      'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--responsible'
    )}
    {renderSkeletonBlock('LeadsRegistryTable__skeleton-chevron')}
  </div>
);

const renderTaskSkeletonCell = () => (
  <div className="LeadsRegistryTable__task-skeleton" aria-hidden="true">
    <div className="LeadsRegistryTable__task-skeleton-head">
      {renderSkeletonBlock(
        'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--task-title'
      )}
      {renderSkeletonBlock('LeadsRegistryTable__skeleton-status-dot')}
    </div>
    {renderSkeletonBlock(
      'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--task-text'
    )}
    {renderSkeletonBlock(
      'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--task-period'
    )}
  </div>
);

const renderTagsSkeletonCell = () => (
  <div className="LeadsRegistryTable__tags-skeleton" aria-hidden="true">
    {renderSkeletonBlock(
      'LeadsRegistryTable__tag-skeleton LeadsRegistryTable__tag-skeleton--primary'
    )}
    {renderSkeletonBlock(
      'LeadsRegistryTable__tag-skeleton LeadsRegistryTable__tag-skeleton--secondary'
    )}
    {renderSkeletonBlock('LeadsRegistryTable__tags-more-skeleton')}
  </div>
);

const renderFirstTouchSkeletonCell = () => (
  <div className="LeadsRegistryTable__first-touch-skeleton" aria-hidden="true">
    {renderSkeletonBlock('LeadsRegistryTable__first-touch-skeleton-icon')}
    <div className="LeadsRegistryTable__first-touch-skeleton-content">
      {renderSkeletonBlock(
        'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--first-touch-source'
      )}
      {renderSkeletonBlock(
        'LeadsRegistryTable__skeleton-line LeadsRegistryTable__skeleton-line--first-touch-date'
      )}
    </div>
  </div>
);

const renderLeadInfoCell = (
  record: LeadsRegistryTableRow,
  onLeadOpen: (leadId: number) => void
) => {
  if (record.kind === 'skeleton') {
    return renderLeadInfoSkeletonCell();
  }

  return (
    <Link
      className="LeadsRegistryTable__client-link"
      to={`${LEAD_CARD_PATH}?leadId=${record.id}&source=registry`}
      // Место лида в списке запоминается в момент ухода в карточку: только
      // здесь ещё известно, где строка стояла на экране.
      onClick={() => onLeadOpen(record.id)}
    >
      <LeadInfoCell leadInfo={record.leadInfo} />
    </Link>
  );
};

const renderLegacyCell = (
  record: LeadsRegistryTableRow,
  renderCell: (columns: LeadsRegistryLegacyColumns) => ReactNode,
  renderSkeletonCell: () => ReactNode
) => {
  if (record.kind === 'skeleton') {
    return renderSkeletonCell();
  }

  return renderCell(record.legacyColumns);
};

const taskStatusLabel: Record<LeadsRegistryTaskStatus, string> = {
  completed: 'Завершена',
  overdue: 'Просрочена',
  planned: 'Запланирована',
};

const taskStatusIcon: Record<LeadsRegistryTaskStatus, ReactNode> = {
  completed: <CheckCircleFilled />,
  overdue: <ExclamationCircleFilled />,
  planned: <ClockCircleOutlined />,
};

const TASK_FALLBACK_TITLE = 'Без названия';

interface TaskStatusIconProps {
  status: LeadsRegistryTaskStatus;
}

// Controlled status hint so the tooltip closes immediately on table scroll and
// destroys its popup on hide, instead of lingering while the row scrolls.
const TaskStatusIcon: FC<TaskStatusIconProps> = ({ status }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeHandler = useCallback(() => setIsOpen(false), []);

  useCloseFloatingOnScroll(isOpen, closeHandler);

  const statusLabel = taskStatusLabel[status];

  return (
    <Tooltip
      title={statusLabel}
      open={isOpen}
      destroyOnHidden
      onOpenChange={setIsOpen}
    >
      <span
        className={`LeadsRegistryTable__task-icon LeadsRegistryTable__task-icon--${status}`}
        role="img"
        aria-label={statusLabel}
      >
        {taskStatusIcon[status]}
      </span>
    </Tooltip>
  );
};

const renderTaskCell = (
  columns: LeadsRegistryLegacyColumns,
  onTaskOpen: (taskId: number) => void,
  openingTaskId: number | null
) => {
  const task = columns.currentTask;

  if (!task) {
    return null;
  }

  const isOpening = openingTaskId === task.id;

  return (
    <button
      type="button"
      className={cn(
        'LeadsRegistryTable__task',
        `LeadsRegistryTable__task--${task.status}`,
        'LeadsRegistryTable__task--clickable',
        { 'LeadsRegistryTable__task--loading': isOpening }
      )}
      disabled={isOpening}
      onClick={() => onTaskOpen(task.id)}
    >
      <div className="LeadsRegistryTable__task-head">
        <span className="LeadsRegistryTable__task-title">
          {task.taskTypeIconId && (
            <FontAwesomeIcon
              className="LeadsRegistryTable__task-type-icon"
              icon={task.taskTypeIconId as IconProp}
              style={{ color: task.taskTypeColor || undefined }}
            />
          )}
          {task.title || TASK_FALLBACK_TITLE}
        </span>
        <TaskStatusIcon status={task.status} />
      </div>
      {task.description && (
        <OverflowTooltip
          className="LeadsRegistryTable__task-text"
          title={task.description}
        >
          {task.description}
        </OverflowTooltip>
      )}
      {task.period && (
        <span className="LeadsRegistryTable__task-period">{task.period}</span>
      )}
    </button>
  );
};

const toTagItem = (tag: LeadsRegistryLegacyColumns['tags'][number]) => ({
  id: tag.id,
  label: tag.title,
  color: tag.color,
  iconId: tag.iconId ?? undefined,
});

const renderTagsCell = (columns: LeadsRegistryLegacyColumns) => (
  <TagsOverflowList items={columns.tags.map(toTagItem)} />
);

const renderFirstTouchCell = (columns: LeadsRegistryLegacyColumns) => {
  const { integrationType, label, dateTime } = columns.firstTouch;

  if (!label && !dateTime) {
    return null;
  }

  return (
    <div className="LeadsRegistryTable__first-touch">
      <SocialIntegrationIcon
        type={integrationType}
        size="sm"
        className="LeadsRegistryTable__first-touch-icon"
      />
      <div className="LeadsRegistryTable__first-touch-content">
        <OverflowTooltip
          className="LeadsRegistryTable__first-touch-source"
          title={label}
        >
          {label ?? LEADS_REGISTRY_EMPTY_VALUE}
        </OverflowTooltip>
        {dateTime && (
          <span className="LeadsRegistryTable__first-touch-date">
            {dateTime}
          </span>
        )}
      </div>
    </div>
  );
};

interface UseLeadsRegistryColumnsParams {
  onLeadOpen(leadId: number): void;
}

export const useLeadsRegistryColumns = ({
  onLeadOpen,
}: UseLeadsRegistryColumnsParams) => {
  const { assigneeOptions, funnels, isFilterDataLoading } =
    useLeadsRegistryFilterData();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { openPreviewTaskModal } = useTaskListContext();
  const [getCrmTask] = useLazyGetCrmTaskQuery();
  const [openingTaskId, setOpeningTaskId] = useState<number | null>(null);

  // Registry rows only carry a lightweight task summary, so resolve the full
  // task by id before opening the shared task modal. Passing only the id would
  // leave the modal empty, because the provider looks the task up in the
  // task-list registry, not in Leads Registry rows.
  const openRegistryTaskHandler = useCallback(
    async (taskId: number) => {
      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        setOpeningTaskId(taskId);

        const task = await getCrmTask({
          id: taskId,
          ActiveBranchId,
        }).unwrap();

        openPreviewTaskModal(task);
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        setOpeningTaskId(null);
      }
    },
    [ActiveBranchId, getCrmTask, openPreviewTaskModal, showNotification]
  );

  return useMemo<ColumnsType<LeadsRegistryTableRow>>(
    () => [
      {
        title: 'Клиент',
        key: 'client',
        width: 300,
        fixed: 'left',
        ellipsis: true,
        render: (_, record) => renderLeadInfoCell(record, onLeadOpen),
      },
      {
        title: 'Этап воронки',
        key: 'funnel',
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          if (record.kind === 'skeleton') return renderFunnelSkeletonCell();

          return (
            <LeadsRegistryFunnelStageCell
              lead={record.sourceLead}
              funnels={funnels}
              isOptionsLoading={isFilterDataLoading}
            />
          );
        },
      },
      {
        title: 'Время ответа',
        key: 'time',
        width: 150,
        ellipsis: true,
        render: (_, record) => {
          if (record.kind === 'skeleton')
            return renderResponseTimeSkeletonCell();

          return <ResponseTimeCell lead={record.sourceLead} />;
        },
      },
      {
        title: 'Ответственный',
        key: 'responsible',
        width: 160,
        ellipsis: true,
        render: (_, record) => {
          if (record.kind === 'skeleton')
            return renderResponsibleSkeletonCell();

          return (
            <LeadsRegistryResponsibleCell
              lead={record.sourceLead}
              options={assigneeOptions}
              isOptionsLoading={isFilterDataLoading}
            />
          );
        },
      },
      {
        title: 'Наличие задач',
        key: 'tasks',
        width: 200,
        ellipsis: true,
        render: (_, record) =>
          renderLegacyCell(
            record,
            (columns) =>
              renderTaskCell(columns, openRegistryTaskHandler, openingTaskId),
            renderTaskSkeletonCell
          ),
      },
      {
        title: 'Теги',
        key: 'tags',
        width: 310,
        ellipsis: true,
        render: (_, record) =>
          renderLegacyCell(record, renderTagsCell, renderTagsSkeletonCell),
      },
      {
        title: 'Первое касание',
        key: 'firstChannelId',
        width: 180,
        ellipsis: true,
        render: (_, record) =>
          renderLegacyCell(
            record,
            renderFirstTouchCell,
            renderFirstTouchSkeletonCell
          ),
      },
    ],
    [
      assigneeOptions,
      funnels,
      isFilterDataLoading,
      onLeadOpen,
      openRegistryTaskHandler,
      openingTaskId,
    ]
  );
};
